import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Printer, Download, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import * as XLSX from 'xlsx';

interface AddressWorksheetProps {
  county: string;
  countyLabel: string;
}

export function AddressWorksheet({ county, countyLabel }: AddressWorksheetProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const downloadWorksheet = async () => {
    setIsLoading(true);
    try {
      // Fetch records for this county that don't have a grantor address yet
      const { data, error } = await supabase
        .from('county_research')
        .select('document_number, filing_date, document_type, grantor_name, grantee_name, legal_description, grantor_address')
        .eq('county', county)
        .is('deleted_at', null)
        .order('filing_date', { ascending: true });

      if (error) throw error;
      if (!data || data.length === 0) {
        toast({ title: 'No records', description: `No records found for ${countyLabel}`, variant: 'destructive' });
        return;
      }

      // Filter to records missing address
      const needsAddress = data.filter(r => !r.grantor_address || r.grantor_address.trim() === '');

      if (needsAddress.length === 0) {
        toast({ title: 'All done', description: 'All records already have addresses!' });
        return;
      }

      // Build worksheet with large address row for handwriting
      const rows = needsAddress.map((r, idx) => ({
        '#': idx + 1,
        'Filing Date': r.filing_date,
        'Doc #': r.document_number,
        'Seller Name': r.grantor_name || '',
        'Seller Address (write in)': '',
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      // Set column widths - extra wide address column for handwriting
      ws['!cols'] = [
        { wch: 4 },   // #
        { wch: 12 },  // Filing Date
        { wch: 12 },  // Doc #
        { wch: 28 },  // Seller Name
        { wch: 50 },  // Address (blank, extra wide)
      ];

      // Set row heights - tall rows so handwriting is legible
      ws['!rows'] = [{ hpt: 20 }]; // header row
      for (let i = 0; i < needsAddress.length; i++) {
        ws['!rows'].push({ hpt: 40 }); // ~0.55 inch tall per data row
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Address Worksheet');

      const filename = `${countyLabel.replace(/[^a-zA-Z0-9]/g, '_')}_Address_Worksheet.xlsx`;
      XLSX.writeFile(wb, filename);

      toast({
        title: 'Worksheet downloaded',
        description: `${needsAddress.length} records needing addresses exported to ${filename}`,
      });
    } catch (err: any) {
      console.error('Worksheet error:', err);
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Printer className="h-5 w-5" />
          Address Worksheet
        </CardTitle>
        <CardDescription>
          Download a printable spreadsheet for {countyLabel} with a blank address column.
          Write in addresses by hand, then photograph the sheet to update records.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={downloadWorksheet} disabled={isLoading} className="gap-2">
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download Address Worksheet
        </Button>
        <p className="text-xs text-muted-foreground mt-3">
          Only records missing a seller address will be included. Print landscape for best results.
        </p>
      </CardContent>
    </Card>
  );
}
